import gql from 'graphql-tag';
import {
  ApolloClient,
  ApolloError,
  NormalizedCacheObject,
  TypedDocumentNode,
} from '@apollo/client';
import { GraphQLError, GraphQLFormattedError } from 'graphql';
import { FastifyLoggerInstance } from 'fastify';
import {
  costGuideLocationVariables,
  costGuideLocation_costGuideLocation_CostGuideLocationSuccess_cityList as costGuideCityList,
  costGuideLocation_costGuideLocation_CostGuideLocationError_errors as costGuideLocationErrors,
  costGuideLocation as costGuideLocationQueryType,
} from '@/__generated__/costGuideLocation';
import { apolloClientQuery } from './utils';

const COST_GUIDE_LOCATION: TypedDocumentNode<
  costGuideLocationQueryType,
  costGuideLocationVariables
> = gql`
  query costGuideLocation($criteria: CostGuideLocationInput!, $limit: Int!) {
    costGuideLocation(criteria: $criteria, limit: $limit) {
      __typename
      ... on CostGuideLocationSuccess {
        cityList {
          slug
          location {
            city
            state {
              name
              stateCode
            }
          }
        }
      }
      ... on CostGuideLocationError {
        errors {
          __typename
          message
        }
      }
    }
  }
`;

type CostGuideLocationQuery = (
  client: ApolloClient<NormalizedCacheObject>,
  log: FastifyLoggerInstance,
  variables: costGuideLocationVariables
) => Promise<
  | {
      cityList: costGuideCityList[];
      error: null;
    }
  | {
      error:
        | ApolloError
        | readonly GraphQLError[]
        | readonly GraphQLFormattedError[]
        | costGuideLocationErrors[];
    }
>;

// make API call and handle any errors. Pass data to Props.
const costGuideLocationQuery: CostGuideLocationQuery = async (client, log, variables) => {
  const { queryResponse, error } = await apolloClientQuery(client, COST_GUIDE_LOCATION, variables);

  // unknown error
  if (error) {
    return {
      error,
    };
  }

  const { data, errors, error: responseError } = queryResponse;

  // GraphQL errors that occur while processing a GraphQL operation (resulting in built-in error codes such as INTERNAL_SERVER_ERROR)
  if (errors && errors.length > 0) {
    return {
      error: errors,
    };
  }

  // Coverage for 'error' property in queryResponse
  if (responseError) {
    return {
      error: responseError,
    };
  }

  // known error
  if (data.costGuideLocation.__typename === 'CostGuideLocationError') {
    log.error(`[GraphQL errors]: ${errors}`);

    return {
      error: data.costGuideLocation.errors,
    };
  }

  return { cityList: data.costGuideLocation.cityList, error: null };
};

export { COST_GUIDE_LOCATION, costGuideLocationQuery };
